import React, { useEffect, useState } from 'react';
import styles from './DocumentSelector.module.scss';
import { handleFileSelection, handleFileValidation } from 'app/utils';
import { useAppSelector, getBusinessArea } from 'app/redux';
import { ReportSection } from './ReportSection';
import {
  DocumentInfo,
  DocumentList,
  IDocumentSelector,
  IFileHandlers
} from './DocumentSelector.interface';
import { DocumentCard } from './DocumentCard';
import {
  createComparison,
  getComparisonStatusStream,
  getDocumentsByUser,
  uploadDocument
} from 'integration';
import { LoadingSpinner } from 'app/components';

type CompareStep = 'uploading' | 'creating' | 'connecting' | null;

export const DocumentSelector: React.FC<IDocumentSelector> = ({
  onComparisonCreated
}) => {
  const currentService = 'document-compare';
  const currentBusinessArea =
    useAppSelector(getBusinessArea) ?? 'cross-functional';

  const [baseDocument, setBaseDocument] = useState<DocumentInfo | undefined>(
    undefined
  );
  const [comparisonDocument, setComparisonDocument] = useState<
    DocumentInfo | undefined
  >(undefined);

  const [reportName, setReportName] = useState('');
  const [baseDocumentMode, setBaseDocumentMode] = useState<'left' | 'right'>(
    'left'
  );
  const [comparisonDocumentMode, setComparisonDocumentMode] = useState<
    'left' | 'right'
  >('left');

  const [baseIsDragging, setBaseIsDragging] = useState(false);
  const [comparisonIsDragging, setComparisonIsDragging] = useState(false);
  const [baseDocumentError, setBaseDocumentError] = useState<
    string | undefined
  >();
  const [comparisonDocumentError, setComparisonDocumentError] = useState<
    string | undefined
  >();

  let defaultReportName = '';
  const bothDocumentsSelected = baseDocument && comparisonDocument;

  const [documentList, setDocumentList] = useState<DocumentList[]>([]);
  const [compareStep, setCompareStep] = useState<CompareStep>(null);

  const [baseDocumentDuplicate, setBaseDocumentDuplicate] = useState<
    DocumentList | undefined
  >();
  const [comparisonDocumentDuplicate, setComparisonDocumentDuplicate] =
    useState<DocumentList | undefined>();

  useEffect(() => {
    if (!documentList.length) {
      setDocumentList([
        {
          documentID: 'loading',
          documentURL: 'loading',
          documentName: 'Loading documents'
        }
      ]);
      getDocumentsByUser(currentBusinessArea, currentService)
        .then((response) => {
          console.log(response);
          setDocumentList(response);
        })
        .catch((error) => {
          console.error('Error fetching documents:', error);
          setDocumentList([
            {
              documentID: 'error',
              documentURL: 'error',
              documentName: 'Error fetching documents'
            }
          ]);
        });
    }
  }, [baseDocumentMode, comparisonDocumentMode]);

  useEffect(() => {
    if (bothDocumentsSelected && !reportName) {
      const getDocumentName = (document: File | string | undefined): string => {
        if (typeof document === 'string') {
          const doc = documentList.find((d) => d.documentID === document);
          return doc ? doc.documentName.replace(/\.[^/.]+$/, '') : 'Unknown';
        }
        return document!.name.replace(/\.[^/.]+$/, '');
      };

      const baseName = getDocumentName(baseDocument.file);
      const comparisonName = getDocumentName(comparisonDocument.file);

      defaultReportName = `${baseName} VS ${comparisonName}`;
      setReportName(defaultReportName);
    }
  }, [baseDocument, comparisonDocument]);

  const checkDuplicateDocument = (
    fileName: string
  ): DocumentList | undefined => {
    return documentList.find(
      (doc) => doc.documentName.toLowerCase() === fileName.toLowerCase()
    );
  };

  const fileHandlers = (
    setDocument: (file: DocumentInfo | undefined) => void,
    setError: (error: string | undefined) => void,
    setIsDragging: (dragging: boolean) => void,
    setDuplicate: (duplicate: DocumentList | undefined) => void
  ): IFileHandlers => {
    const handleSelectionClick = (e: { stopPropagation: () => void }): void => {
      e.stopPropagation();
      setError(undefined);
      handleFileSelection(handleFileChange, currentService);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      const { files } = e.target;
      const selectedFile: File | undefined = files ? files[0] : undefined;

      if (selectedFile !== undefined) {
        const duplicate = checkDuplicateDocument(selectedFile.name);
        if (duplicate) {
          setDuplicate(duplicate);
          return;
        }

        const validation = handleFileValidation(
          selectedFile,
          'document-compare-v2'
        );
        if (validation.valid) {
          const doc = {
            file: selectedFile,
            shouldUpload: true
          };
          setDocument(doc);
        } else {
          const errorMessage = validation.error;
          console.error(errorMessage);
          setError(errorMessage);
        }
      }
    };

    const handleFileDrop = (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];

      if (file !== undefined) {
        const duplicate = checkDuplicateDocument(file.name);
        if (duplicate) {
          setDuplicate(duplicate);
          return;
        }

        const validation = handleFileValidation(file, 'document-compare-v2');
        if (validation.valid) {
          const doc = {
            file,
            shouldUpload: true
          };
          setDocument(doc);
        } else {
          const errorMessage = validation.error;
          console.error(errorMessage);
          setError(errorMessage);
        }
      }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      setError(undefined);
    };

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      setIsDragging(false);
    };

    const handleDeleteFile = (): void => {
      setDocument(undefined);
      setError(undefined);
      setDuplicate(undefined);
      setReportName('');
    };

    const onDocumentSelect = (file: DocumentInfo): void => {
      setDocument(file);
    };

    const clearDuplicate = (): void => {
      setDuplicate(undefined);
    };

    return {
      handleSelectionClick,
      handleFileDrop,
      handleDragOver,
      handleDragEnter,
      handleDragLeave,
      handleDeleteFile,
      onDocumentSelect,
      clearDuplicate
    };
  };

  const uploadDocumentIfNeeded = async (
    document: DocumentInfo
  ): Promise<string> => {
    if (document.shouldUpload) {
      return await uploadDocument(
        currentBusinessArea,
        currentService,
        document.file
      )
        .then((response) => {
          console.log('Document uploaded:', response);
          return response.documentID;
        })
        .catch((error) => {
          console.error('Error uploading document:', error);
          throw error;
        });
    } else {
      return await Promise.resolve(document.documentId!);
    }
  };

  const handleCompareDocuments = async (): Promise<void> => {
    if (!baseDocument || !comparisonDocument) return;

    try {
      setCompareStep('uploading');
      const [baseDocumentId, comparisonDocumentId] = await Promise.all([
        uploadDocumentIfNeeded(baseDocument),
        uploadDocumentIfNeeded(comparisonDocument)
      ]);

      setCompareStep('creating');
      const response = await createComparison(
        currentBusinessArea,
        currentService,
        {
          name: reportName,
          document1Id: baseDocumentId,
          document2Id: comparisonDocumentId
        }
      );

      setCompareStep('connecting');
      const streamReader = await getComparisonStatusStream(
        currentBusinessArea,
        currentService,
        response.comparisonId
      );

      setCompareStep(null);
      onComparisonCreated(response.comparisonId, streamReader);
    } catch (error) {
      console.error('Error creating comparison:', error);
      setCompareStep(null);
    }
  };

  return (
    <>
      {!compareStep && (
        <>
          {bothDocumentsSelected && (
            <ReportSection
              reportName={reportName}
              onReportNameChange={setReportName}
              onCompareDocuments={handleCompareDocuments}
            />
          )}

          <div className={styles.container}>
            <DocumentCard
              title="Base Document"
              mode={baseDocumentMode}
              onModeChange={setBaseDocumentMode}
              selectedFile={baseDocument}
              errorText={baseDocumentError}
              fileHandlers={fileHandlers(
                setBaseDocument,
                setBaseDocumentError,
                setBaseIsDragging,
                setBaseDocumentDuplicate
              )}
              isDragging={baseIsDragging}
              documentList={documentList}
              duplicateDocument={baseDocumentDuplicate}
            />

            <DocumentCard
              title="Comparison Document"
              mode={comparisonDocumentMode}
              onModeChange={setComparisonDocumentMode}
              selectedFile={comparisonDocument}
              errorText={comparisonDocumentError}
              fileHandlers={fileHandlers(
                setComparisonDocument,
                setComparisonDocumentError,
                setComparisonIsDragging,
                setComparisonDocumentDuplicate
              )}
              isDragging={comparisonIsDragging}
              documentList={documentList}
              duplicateDocument={comparisonDocumentDuplicate}
            />
          </div>
        </>
      )}

      {compareStep && (
        <div className={styles['compare-progress']}>
          <LoadingSpinner
            containerStyle={{
              position: 'relative',
              width: 'auto',
              height: 'auto',
              backdropFilter: 'unset'
            }}
          />
          <div className={styles['compare-progress-steps']}>
            {(
              [
                { key: 'uploading', label: 'Uploading documents...' },
                {
                  key: 'creating',
                  label: 'Creating comparison record in DB...'
                },
                { key: 'connecting', label: 'Connecting to status stream...' }
              ] as Array<{ key: CompareStep; label: string }>
            ).map(({ key, label }) => {
              const steps = ['uploading', 'creating', 'connecting'];
              const isDone =
                compareStep !== null &&
                steps.indexOf(compareStep) > steps.indexOf(key as string);
              const isActive = compareStep === key;
              const cls = [
                styles['compare-progress-step'],
                isActive ? styles['compare-progress-step--active'] : '',
                isDone ? styles['compare-progress-step--done'] : ''
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <span key={key} className={cls}>
                  {label}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};
